import React, { useState } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trophy, Users, Target, ExternalLink, ArrowRight, Loader2, CheckCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuthState';
import { useToast } from '@/hooks/use-toast';
// COMMENTED OUT: ID Verification disabled
// import { IDVerificationPopup } from '@/components/id-verification-popup';
import { CountdownTimer } from '@/components/challenges/CountdownTimer';
import { UsernameSelectionModal } from '@/components/UsernameSelectionModal';
import Navbar from '@/components/navbar';
import Footer from '@/components/footer';

interface Challenge {
  id: string;
  name: string;
  description?: string;
  type: 'acquisition' | 'participation' | 'growth' | 'marketing';
  status: 'draft' | 'live' | 'ended' | 'scheduled';
  startDateTime?: string;
  targetValue: number;
  prizeDetails?: {
    '1st'?: { item: string; value: number };
    '2nd'?: { item: string; value: number };
    '3rd'?: { item: string; value: number };
  };
  totalParticipants: number;
}

function ChallengeCard({ challenge, onViewChallenge }: { challenge: Challenge; onViewChallenge: (id: string) => void }) {
  const { user, session } = useAuth();
  const { toast } = useToast();
  const [isJoining, setIsJoining] = useState(false);
  // COMMENTED OUT: ID Verification disabled
  // const [showIdVerification, setShowIdVerification] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);

  // Check if user has already joined this challenge
  const { data: userProgress } = useQuery({
    queryKey: ["/api/v1/challenges/progress", challenge.id, user?.id],
    queryFn: async () => {
      const headers: Record<string, string> = {};
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(`/api/v1/challenges/${challenge.id}/my-progress`, { headers });
      if (response.status === 404) {
        return null; // User hasn't joined yet
      }
      if (!response.ok) {
        throw new Error('Failed to fetch user progress');
      }
      return response.json();
    },
    enabled: !!user?.id && !!session?.access_token,
    staleTime: 30000, // Cache for 30 seconds
  });

  const hasJoined = !!userProgress;

  const handleCardClick = async () => {
    // If challenge has ended or is scheduled, prevent any action
    if (challenge.status === 'ended' || challenge.status === 'scheduled') {
      return;
    }

    // If user has already joined, take them directly to challenge page
    if (hasJoined) {
      onViewChallenge(challenge.id);
      return;
    }

    // If user is logged in but hasn't joined, proceed with joining first
    if (user && session?.access_token) {
      setIsJoining(true);
      try {
        const response = await fetch(`/api/v1/challenges/${challenge.id}/join`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          toast({
            title: "🎉 Challenge Joined!",
            description: `You've successfully joined ${challenge.name}. Redirecting...`,
          });

          // COMMENTED OUT: ID verification disabled
          // // After successful join, check ID verification status
          // try {
          //   console.log('[CHALLENGE LISTING] Successfully joined challenge, now checking ID verification status');
          //   const userResponse = await fetch(`/api/auth/user`, {
          //     headers: {
          //       'Authorization': `Bearer ${session.access_token}`
          //     }
          //   });

          //   if (userResponse.ok) {
          //     const userData = await userResponse.json();
          //     if (!userData.idVerified) {
          //       console.log('[CHALLENGE LISTING] User not verified after joining - showing ID verification popup');
          //       setShowIdVerification(true);
          //       setIsJoining(false);
          //       return;
          //     } else {
          //       console.log('[CHALLENGE LISTING] User already verified, proceeding to check username');
          //     }
          //   } else {
          //     console.log('[CHALLENGE LISTING] Cannot check verification status after join, showing popup for safety');
          //     setShowIdVerification(true);
          //     setIsJoining(false);
          //     return;
          //   }
          // } catch (verificationError) {
          //   console.error('[CHALLENGE LISTING] Error checking verification after join:', verificationError);
          //   // Show verification popup if we can't check status for security
          //   setShowIdVerification(true);
          //   setIsJoining(false);
          //   return;
          // }

          // Check if user needs username setup
          try {
            console.log('[CHALLENGE LISTING] Checking if user needs to set username');
            const usernameCheckResponse = await fetch('/api/v1/challenges/user/needs-username-setup', {
              headers: {
                'Authorization': `Bearer ${session.access_token}`
              }
            });

            if (usernameCheckResponse.ok) {
              const usernameData = await usernameCheckResponse.json();
              console.log('[CHALLENGE LISTING] Username setup data:', usernameData);

              if (usernameData.needsSetup) {
                console.log('[CHALLENGE LISTING] User needs username setup - showing username modal');
                setShowUsernameModal(true);
                setIsJoining(false);
                return;
              } else {
                console.log('[CHALLENGE LISTING] User has custom username, proceeding to challenge page');
              }
            }
          } catch (usernameError) {
            console.error('[CHALLENGE LISTING] Error checking username setup:', usernameError);
            // Continue to challenge page even if check fails
          }

          // Redirect to challenge page after successful join and verification check
          setTimeout(() => {
            onViewChallenge(challenge.id);
          }, 1500);
        } else {
          const errorData = await response.json();

          if (errorData.error?.includes('Already joined')) {
            toast({
              title: "Already Joined",
              description: "You're already participating in this challenge. Taking you to the challenge dashboard...",
            });
            setTimeout(() => {
              onViewChallenge(challenge.id);
            }, 1500);
          } else {
            throw new Error(errorData.error || 'Failed to join challenge');
          }
        }
      } catch (error) {
        console.error('Failed to join challenge:', error);
        toast({
          title: "Join Failed",
          description: error instanceof Error ? error.message : 'Failed to join challenge. Please try again.',
          variant: "destructive",
        });
        setIsJoining(false);
      }
    } else {
      // If user is not logged in, go to challenge detail page
      onViewChallenge(challenge.id);
    }
  };

  // COMMENTED OUT: ID Verification disabled
  // // Handle successful ID verification
  // const handleIdVerificationSuccess = async () => {
  //   console.log('[CHALLENGE LISTING ID_VERIFICATION] ID verification successful, attempting to join challenge');
  //   setShowIdVerification(false);

  //   // After successful ID verification, automatically attempt to join the challenge
  //   await handleCardClick();
  // };

  // Handle successful username save
  const handleUsernameSaved = (username: string) => {
    console.log('[CHALLENGE LISTING] Username saved successfully:', username, ', proceeding to challenge page');
    setShowUsernameModal(false);
    onViewChallenge(challenge.id);
  };

  const isEnded = challenge.status === 'ended';
  const isScheduled = challenge.status === 'scheduled';

  return (
    <>
      <Card
        className={`group transition-all duration-300 border-2 ${
          isEnded || isScheduled
            ? 'opacity-60 cursor-not-allowed border-gray-300'
            : 'hover:shadow-xl hover:border-blue-200 cursor-pointer'
        }`}
        onClick={handleCardClick}
      >
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg">
              <Trophy className="h-5 w-5 text-white" />
            </div>
            <div className="flex flex-col gap-1">
              <Badge
                variant={challenge.status === 'live' ? 'default' : 'secondary'}
                className={`capitalize ${
                  challenge.status === 'ended' ? 'bg-gray-500 text-white' :
                  challenge.status === 'scheduled' ? 'bg-orange-500 text-white' : ''
                }`}
              >
                {challenge.status === 'live' ? '🔴 Live' :
                 challenge.status === 'ended' ? '⏹️ Ended' :
                 challenge.status === 'scheduled' ? '⏰ Starting Soon' :
                 challenge.status}
              </Badge>
              {isScheduled && challenge.startDateTime && (
                <CountdownTimer targetDate={challenge.startDateTime} />
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Target</div>
            <div className="text-2xl font-bold text-purple-600">{challenge.targetValue}</div>
          </div>
        </div>
        <CardTitle className="text-xl group-hover:text-blue-600 transition-colors">
          {challenge.name}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {challenge.description && (
          <p className="text-gray-600 mb-4 line-clamp-2">{challenge.description}</p>
        )}

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Users className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">
              {challenge.totalParticipants} Participants
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Target className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">Referral Challenge</span>
          </div>
        </div>

        {/* Prize Details */}
        {challenge.prizeDetails && Object.keys(challenge.prizeDetails).length > 0 && (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-3 rounded-lg mb-4">
            <div className="text-sm font-semibold text-gray-700 mb-2">🏆 Prizes:</div>
            <div className="space-y-1">
              {challenge.prizeDetails?.['1st'] && (
                <div className="text-sm">
                  🥇 1st: {challenge.prizeDetails['1st'].item}
                </div>
              )}
              {challenge.prizeDetails?.['2nd'] && (
                <div className="text-sm">
                  🥈 2nd: {challenge.prizeDetails['2nd'].item}
                </div>
              )}
              {challenge.prizeDetails?.['3rd'] && (
                <div className="text-sm">
                  🥉 3rd: {challenge.prizeDetails['3rd'].item}
                </div>
              )}
            </div>
          </div>
        )}

        <Button
          className={`w-full transition-colors ${
            isEnded || isScheduled
              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
              : hasJoined
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'group-hover:bg-blue-600'
          }`}
          size="lg"
          disabled={isJoining || isEnded || isScheduled}
          data-testid={`button-${isEnded ? 'ended' : isScheduled ? 'starting-soon' : hasJoined ? 'entered' : 'join'}-challenge`}
        >
          {isEnded ? (
            <>
              Challenge Ended
            </>
          ) : isScheduled ? (
            <>
              Starting Soon
            </>
          ) : isJoining ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Joining Challenge...
            </>
          ) : hasJoined ? (
            <>
              <CheckCircle className="h-4 w-4 mr-2" />
              Entered
              <ArrowRight className="h-4 w-4 ml-2" />
            </>
          ) : user ? (
            <>
              <Trophy className="h-4 w-4 mr-2" />
              Join Challenge
              <ArrowRight className="h-4 w-4 ml-2" />
            </>
          ) : (
            <>
              <ExternalLink className="h-4 w-4 mr-2" />
              View Challenge
              <ArrowRight className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </CardContent>
    </Card>

    {/* COMMENTED OUT: ID Verification disabled */}
    {/* {/* ID Verification Popup */}
    {/* <IDVerificationPopup
      isOpen={showIdVerification}
      onClose={() => setShowIdVerification(false)}
      onVerified={handleIdVerificationSuccess}
      challengeName={challenge?.name || 'Challenge'}
    /> */}

    {/* Username Selection Modal */}
    <UsernameSelectionModal
      isOpen={showUsernameModal}
      currentUsername={user?.username || ''}
      onUsernameSet={handleUsernameSaved}
      onClose={undefined}
    />
  </>
  );
}

function ChallengesListingPage() {
  const [, navigate] = useLocation();

  // Fetch active challenges
  const { data: challengeData, isLoading, error } = useQuery({
    queryKey: ['challenges', 'active'],
    queryFn: async () => {
      const response = await fetch('/api/v1/challenges/active');
      if (!response.ok) {
        throw new Error('Failed to fetch challenges');
      }
      return response.json();
    }
  });

  const handleViewChallenge = (challengeId: string) => {
    navigate(`/challenges/${challengeId}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading challenges...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-red-600 mb-4">Failed to load challenges</p>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Helper function to convert API prizes format to component format
  const convertPrizesToPrizeDetails = (prizes: any) => {
    if (!prizes) return null;

    const prizeDetails: any = {};

    // Extract value from strings like "R1000 KFC Voucher" or handle plain strings
    const extractPrizeValue = (prizeString: string) => {
      const match = prizeString.match(/R(\d+)/);
      return match ? parseInt(match[1]) : 0;
    };

    if (prizes.prize1) {
      prizeDetails['1st'] = {
        item: prizes.prize1,
        value: extractPrizeValue(prizes.prize1)
      };
    }

    if (prizes.prize2) {
      prizeDetails['2nd'] = {
        item: prizes.prize2,
        value: extractPrizeValue(prizes.prize2)
      };
    }

    if (prizes.prize3) {
      prizeDetails['3rd'] = {
        item: prizes.prize3,
        value: extractPrizeValue(prizes.prize3)
      };
    }

    return Object.keys(prizeDetails).length > 0 ? prizeDetails : null;
  };

  const challenges = challengeData?.challenges ? challengeData.challenges.map((challenge: any) => ({
    ...challenge,
    id: challenge.id?.toString() || '1',
    targetValue: challenge.targetValue || 1000,
    totalParticipants: challenge.totalParticipants || 0,
    prizeDetails: convertPrizesToPrizeDetails(challenge.prizes),
    status: challenge.status || 'live',
    type: challenge.type || 'acquisition' as const
  })) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Trophy className="h-12 w-12" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Challenges
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Join exciting referral challenges and compete for amazing prizes!
              Refer friends and climb the leaderboard.
            </p>
          </div>
        </div>
      </div>

      {/* Challenges Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {challenges.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {challenges.map((challenge) => (
              <ChallengeCard
                key={challenge.id}
                challenge={challenge}
                onViewChallenge={handleViewChallenge}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Trophy className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Challenges Available</h3>
            <p className="text-gray-600 mb-6">
              There are currently no challenges available. Check back soon for new competitions!
            </p>
            <Button onClick={() => navigate('/')}>
              Back to Home
            </Button>
          </div>
        )}

      </div>

      <Footer />
    </div>
  );
}

export default ChallengesListingPage;